import { Controller } from "@hotwired/stimulus"

// Same lookup Turbo uses: the `authenticity_token` cookie first, then the meta tag.
function currentCsrfToken() {
  const fromCookie = document.cookie
    .split("; ")
    .find((row) => row.startsWith("authenticity_token="))
    ?.split("=")[1]
  return fromCookie ? decodeURIComponent(fromCookie) : document.querySelector('meta[name="csrf-token"]')?.content
}

// "What stopped you?" after an abandoned Flitt checkout
// (courses/flitt_payments/checkout_feedbacks/_prompt).
//
// The layout renders the prompt hidden. Once the page is on screen this claims it (POST)
// and opens on a 201 (the first showing of the order) or a 200 (another tab of the same
// return claimed it a moment earlier); anything else removes it, so a later page never
// shows it again. A tab rendered in the background waits until it is shown. A failed
// claim leaves the prompt hidden, and the next page asks again.
//
// Answering and closing both submit the form through Turbo, whose response removes the
// element (and a broadcast removes it from other tabs); the sheet slides away as the
// submit starts.
export default class extends Controller {
  static targets = ["panel", "dismissButton", "otherToggle", "other", "comment"]
  static values = { claimUrl: String }

  connect() {
    this.onVisibilityChange = () => {
      if (document.visibilityState === "visible") this.claim()
    }
    this.onKeydown = (event) => {
      if (event.key === "Escape" && this.isOpen) this.dismiss()
    }
    document.addEventListener("keydown", this.onKeydown)

    if (document.visibilityState === "visible") {
      this.claim()
    } else {
      document.addEventListener("visibilitychange", this.onVisibilityChange)
    }
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    document.removeEventListener("keydown", this.onKeydown)
    if (this.isOpen) document.body.classList.remove("overflow-hidden")
  }

  async claim() {
    if (this.claiming) return
    this.claiming = true
    document.removeEventListener("visibilitychange", this.onVisibilityChange)

    let response
    try {
      response = await fetch(this.claimUrlValue, {
        method: "POST",
        credentials: "same-origin",
        headers: { "X-CSRF-Token": currentCsrfToken() }
      })
    } catch {
      return
    }

    if (response.status === 201 || response.status === 200) {
      this.open()
    } else {
      this.element.remove()
    }
  }

  open() {
    this.element.hidden = false
    document.body.classList.add("overflow-hidden")
    this.isOpen = true
    // Two frames: the transition needs the unhidden element painted in its start state.
    requestAnimationFrame(() => requestAnimationFrame(() => {
      this.element.dataset.open = ""
    }))
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    delete this.element.dataset.open
    document.body.classList.remove("overflow-hidden")
  }

  dismiss() {
    this.panelTarget.requestSubmit(this.dismissButtonTarget)
  }

  showOther() {
    this.otherToggleTarget.hidden = true
    this.otherToggleTarget.setAttribute("aria-expanded", "true")
    this.otherTarget.hidden = false
    this.commentTarget.focus()
  }
}
